import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const amountFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRateHistory = (material) => {
  if (Array.isArray(material?.lastRateHistory) && material.lastRateHistory.length > 0) {
    return material.lastRateHistory;
  }

  if (material?.lastRate === undefined || material?.lastRate === null) return [];

  return [{
    rfqId: material.foundInRfq,
    lastRate: material.lastRate,
    lastRateDate: material.lastRateDate,
    projectName: "-",
  }];
};

const openRfqInNewTab = (rfqId) => {
  if (!rfqId) return;

  const url = `${window.location.origin}/rfqs/${rfqId}/Selected%20Rates`;
  const newTab = window.open(url, "_blank", "noopener,noreferrer");
  if (newTab) newTab.opener = null;
};

const dateRangeOptions = [
  { key: "3m", label: "Last 3 Months", months: 3 },
  { key: "6m", label: "Last 6 Months", months: 6 },
  { key: "12m", label: "Last 12 Months", months: 12 },
];

const isWithinDateRange = (value, months) => {
  if (!months) return true;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return date >= cutoff;
};

const LastRateHistoryModal = ({ material, onClose }) => {
  const history = getRateHistory(material);
  const [selectedRange, setSelectedRange] = useState("all");
  const [projectSearch, setProjectSearch] = useState("");

  const selectedMonths = dateRangeOptions.find(
    (option) => option.key === selectedRange,
  )?.months;
  const normalizedProjectSearch = projectSearch.trim().toLowerCase();
  const filteredHistory = history.filter((rate) => {
    const projectName = String(rate?.projectName || "").replace(/\|\|/g, ", ");
    const matchesProject = !normalizedProjectSearch ||
      projectName.toLowerCase().includes(normalizedProjectSearch);

    return matchesProject && isWithinDateRange(rate?.lastRateDate, selectedMonths);
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!material || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 px-4 py-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-labelledby="last-rate-history-title"
        aria-modal="true"
        className="flex max-h-[82vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        role="dialog"
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id="last-rate-history-title" className="text-base font-semibold text-gray-900">
              Last rate history
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Material code: <span className="font-semibold text-gray-700">{material.code || "-"}</span>
              <span className="mx-2 text-gray-300">|</span>
              {filteredHistory.length} historical {filteredHistory.length === 1 ? "rate" : "rates"} found
            </p>
          </div>
          <button
            aria-label="Close last rate history"
            className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </header>

        <div className="border-b border-gray-100 px-6 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-gray-600">Date</span>
              <button
                type="button"
                onClick={() => setSelectedRange("all")}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  selectedRange === "all"
                    ? "border-orange-500 bg-orange-500 text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-orange-500 hover:text-orange-500"
                }`}
              >
                All time
              </button>
              {dateRangeOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSelectedRange(option.key)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    selectedRange === option.key
                      ? "border-orange-500 bg-orange-500 text-white"
                      : "border-gray-200 bg-white text-gray-600 hover:border-orange-500 hover:text-orange-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <label className="relative block w-full sm:max-w-[280px]">
              <span className="sr-only">Search project</span>
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search"
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
                placeholder="Search project"
                className="h-9 w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-xs text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500"
              />
            </label>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full min-w-[760px] table-fixed text-left text-xs">
            <thead className="sticky top-0 z-10 bg-gray-100 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="w-16 px-5 py-3 font-semibold">Sr No</th>
                <th className="w-28 px-4 py-3 font-semibold">RFQ ID</th>
                <th className="w-36 px-4 py-3 text-right font-semibold">Last Rate</th>
                <th className="w-36 px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Project</th>
                <th className="w-28 px-4 py-3 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {filteredHistory.map((rate, index) => (
                <tr key={`${rate.rfqId}-${rate.lastRate}-${index}`} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">#{rate.rfqId || "-"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-orange-900">
                    Rs. {amountFormatter.format(Number(rate.lastRate) || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(rate.lastRateDate)}</td>
                  <td className="px-4 py-3 break-words">
                    {String(rate.projectName || "-").replace(/\|\|/g, ", ")}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      className="whitespace-nowrap rounded-md border border-orange-200 bg-orange-50 px-3 py-1.5 font-semibold text-orange-700 hover:orange-blue-300 hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={!rate.rfqId}
                      onClick={() => openRfqInNewTab(rate.rfqId)}
                      type="button"
                    >
                      Open RFQ
                    </button>
                  </td>
                </tr>
              ))}
              {filteredHistory.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-5 py-10 text-center text-sm text-gray-500">
                    No rate history matches the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>,
    document.body
  );
};

export default LastRateHistoryModal;
